import { Fragment, useEffect, useRef } from 'react';
import BirthdayDocument from '../shared/birthday-document';
import Citizenship from '../shared/citizenship';
import EditData from '../shared/edit-data';
import FamilyCoexistence from '../shared/family-coexistence';
import Fatherhood from '../shared/fatherhood';
import AccordionGenerality from '../shared/generality';
import Motherhood from '../shared/motherhood';
import Document from '../shared/document';
import AddressForeign from '../shared/address-foreign';
import CivilStatus from '../shared/civil-status';
import AccordionGeneralityResearch from '../shared/generality-research';
import FamilyCoexistenceList from './table';
import { useFormikContext } from 'formik';
import { CommonInterfaceSubscription } from 'subscription/view-models/common-interface-subscription';
import { CODE_STATE_ITALIAN } from 'shell/constants/code-state-italian';

interface SubscriptionBirthProps {
  isCohabitation?: boolean;
  isAire?: boolean;
  withoutFamily?: boolean;
  isCitizenship?: boolean;
  generalityWithResearch?: boolean;
  isTransferNew?: boolean;
  isTransferAggregation?: boolean;
  disabledFromAct?: boolean;
  isFromCivilStatus?: boolean;
  isSubscription?: boolean;
}

const SubscriptionBirthFamily = ({
  isCohabitation = false,
  isAire = false,
  withoutFamily = false,
  isCitizenship = false,
  generalityWithResearch = false,
  isTransferNew = false,
  disabledFromAct = false,
  isTransferAggregation,
  isFromCivilStatus = false,
  isSubscription = false,
}: SubscriptionBirthProps): React.ReactElement => {
  const formContext = useFormikContext<CommonInterfaceSubscription>();

  const refContext = useRef<CommonInterfaceSubscription | null>(null);

  refContext.current = formContext.values || null;

  useEffect(() => {
    const [citizenshipFather] = formContext.values.fatherhood?.dati3002?.cittadinanza || [];
    const [citizenshipMother] = formContext.values.motherhood?.dati3002?.cittadinanza || [];

    // cittadinanza italiana se padre o madre italiano/a
    const isItalianFather = formContext.values.fatherhood?.dati3002?.cittadinanza?.find(
      (el) => el.codiceStato === CODE_STATE_ITALIAN
    );

    const isItalianMother = formContext.values.motherhood?.dati3002?.cittadinanza?.find(
      (el) => el.codiceStato === CODE_STATE_ITALIAN
    );

    if (citizenshipFather || citizenshipMother) {
      let state = null;

      // Se padre o madre italiano/a è riconosciuto da entrambi
      if (
        (isItalianFather || isItalianMother) &&
        formContext.values.editDataControl?.requested === '1'
      ) {
        if (isItalianFather) {
          state = { id: citizenshipFather.codiceStato, value: citizenshipFather.descrizioneStato };
        }
        if (isItalianMother) {
          state = { id: citizenshipMother.codiceStato, value: citizenshipMother.descrizioneStato };
        }
      }

      // Se cittadinanza padre e madre uguali allora quella del padre
      if (
        citizenshipFather &&
        citizenshipMother &&
        citizenshipFather.codiceStato === citizenshipMother.codiceStato &&
        formContext.values.editDataControl?.requested === '1'
      ) {
        state = { id: citizenshipFather.codiceStato, value: citizenshipFather.descrizioneStato };
      }

      // Se solo madre cittadinanza
      if (
        citizenshipMother &&
        !citizenshipFather &&
        formContext.values.editDataControl?.requested === '3'
      ) {
        state = { id: citizenshipMother.codiceStato, value: citizenshipMother.descrizioneStato };
      }

      // Se solo padre cittadinanza
      if (
        !citizenshipMother &&
        citizenshipFather &&
        formContext.values.editDataControl?.requested === '2'
      ) {
        state = {
          id: citizenshipFather.codiceStato,
          value: citizenshipFather.descrizioneStato,
        };
      }

      formContext.setFieldValue('citizenship', {
        validity: refContext.current?.generality?.birthDate,
        state,
      });
    }
  }, [
    formContext.values.fatherhood,
    formContext.values.motherhood,
    formContext.values.editDataControl?.requested,
  ]);

  useEffect(() => {
    formContext.setFieldValue('editDataControl', {
      ...(refContext.current?.editDataControl || {}),
      assume:
        refContext.current?.editDataControl?.requested === '4'
          ? 4
          : refContext.current?.editDataControl?.assume || undefined,
    });
  }, [formContext.values.editDataControl?.requested]);

  return (
    <Fragment>
      <FamilyCoexistenceList
        values={{ identifierAnpr: formContext.values.familyCoexistence?.identifierAnpr }}
        formContext={formContext}
      />
      <AddressForeign isAire={isAire} />
      <EditData isCohabitation={isCohabitation} isAire={isAire} disabledFromAct={disabledFromAct} />
      {generalityWithResearch ? (
        <AccordionGeneralityResearch
          isCohabitation={isCohabitation}
          isAire={isAire}
          isSubscription={isSubscription}
        />
      ) : (
        <AccordionGenerality
          isCohabitation={isCohabitation}
          isAire={isAire}
          disabled={disabledFromAct}
          isFromCivilStatus={isFromCivilStatus}
          isSubscription={isSubscription}
        />
      )}
      <BirthdayDocument
        isAire={isAire}
        isTransferNew={isTransferNew}
        isTransferAggregation={isTransferAggregation}
        disabled={disabledFromAct}
      />
      <Fatherhood
        isCohabitation={isCohabitation}
        isAire={isAire}
        isTransferNew={isTransferNew}
        disabled={disabledFromAct}
      />
      <Motherhood
        isCohabitation={isCohabitation}
        isAire={isAire}
        isTransferNew={isTransferNew}
        disabled={disabledFromAct}
      />
      <Citizenship isTransferNew={isTransferNew} isTransferAggregation={isTransferAggregation} />
      <CivilStatus isCitizenship={isCitizenship} />
      <Document isAire={isAire} />
      <FamilyCoexistence
        isCohabitation={isCohabitation}
        withoutFamily={withoutFamily}
        disabledFromAct={disabledFromAct}
      />
    </Fragment>
  );
};

SubscriptionBirthFamily.displayName = 'Subscription Birth Family component';

export default SubscriptionBirthFamily;
